import * as React from 'react';
import { NumericFormat } from 'react-number-format';
import { Input } from './input';
import { cn } from '@/lib/utils';

type FormatType = 'currency' | 'percentage';

interface FormattedInputProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  formatType?: FormatType;
  className?: string;
}

function FormattedInput({
  value,
  onChange,
  formatType,
  className,
  type,
  id,
  placeholder,
  disabled,
  ...props
}: FormattedInputProps) {
  const handleValueChange = React.useCallback(
    (values: { floatValue: number | undefined; formattedValue: string }) => {
      // Convert to string, handling undefined/null
      const stringValue = values.floatValue?.toString() || '';
      onChange(stringValue);
    },
    [onChange],
  );

  // Convert string value to number for NumberFormat
  const numericValue = React.useMemo(() => {
    if (!value || value === '') return undefined;
    const num = parseFloat(value);
    return isNaN(num) ? undefined : num;
  }, [value]);

  // Extract only the props that NumericFormat accepts
  // NumericFormat only accepts type: "text" | "password" | "tel"
  const numericFormatProps: {
    id?: string;
    placeholder?: string;
    disabled?: boolean;
    type?: 'text' | 'password' | 'tel';
  } = {
    id,
    placeholder,
    disabled,
    // NumericFormat requires type="text", not "number" or other types
    type: 'text',
  };

  if (formatType === 'currency') {
    return (
      <NumericFormat
        {...numericFormatProps}
        customInput={Input}
        value={numericValue}
        onValueChange={handleValueChange}
        thousandSeparator={true}
        prefix="$"
        decimalScale={2}
        fixedDecimalScale={true}
        allowNegative={true}
        className={cn(className)}
      />
    );
  }

  if (formatType === 'percentage') {
    return (
      <NumericFormat
        {...numericFormatProps}
        customInput={Input}
        value={numericValue}
        onValueChange={handleValueChange}
        suffix="%"
        decimalScale={2}
        allowNegative={false}
        className={cn(className)}
      />
    );
  }

  // Fallback to regular input if no format type
  const { ref: _ref, ...restProps } = props as React.ComponentProps<'input'>;
  return (
    <Input
      {...restProps}
      id={id}
      type={type}
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(className)}
    />
  );
}

export { FormattedInput };
export type { FormattedInputProps, FormatType };
